/**
 * Data layer for the COMPANY panel's Self Development section.
 *
 * Six tools the app groups together — Gratitude, Cleanse, Journal, Goals,
 * Vision Board and Affirmations — reported as one practice rather than six
 * features, because that is what they are: the headline number is how many
 * people do ANY of this, not how many gratitudes got written.
 *
 * Nothing anybody wrote reaches this layer. No gratitude, no journal page, no
 * thought record, no goal, no affirmation, no board — the endpoints never
 * project their text, their tags, their categories or their images. What comes
 * back is what somebody DID: that an entry exists, when, whether they saved it,
 * whether they finished the exercise, whether they achieved the goal.
 *
 * The stored `positivity` / sentiment read on those entries is deliberately not
 * exposed either. Mood check-ins are the self-reported wellbeing signal and
 * have their own section; a sentiment score inferred from private diary
 * entries is not the same thing and does not belong on an employer's screen.
 *
 * Backed by the company/v1 self-development-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter can narrow the
 * whole page.
 */

import {
  getCompanySelfDevelopmentDashboard,
  getCompanySelfDevelopmentTool,
  getCompanySelfDevelopmentUsers,
  getCompanySelfDevelopmentUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Section overview — cross-tool first, then a row per tool. */
export const adaptOverview = (data) => ({
  totalUsers: data.totalUsers ?? 0,
  activeUsers: data.activeUsers ?? 0,
  adoption: data.adoption ?? 0,
  totalEntries: data.totalEntries ?? 0,
  avgEntriesPerActiveUser: data.avgEntriesPerActiveUser ?? 0,

  toolBreakdown: data.toolBreakdown || [],

  // Breadth of practice: one tool is a try, four is a habit.
  toolsPerUser: data.toolsPerUser || [],
  multiToolUsers: data.multiToolUsers ?? 0,

  // Distinct days across the whole section, not per tool — a goal and a
  // journal entry on the same afternoon is one day of practice.
  consistencySplit: data.consistencySplit || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  entriesTrend: (data.dailyTrend || []).map((day) => day.entries || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

/**
 * One tool's page. The common block is identical for all five so the pages read
 * the same; everything else is passed through untouched and the page decides
 * what to render for its own tool.
 */
export const adaptTool = (data) => ({
  ...data,
  tool: data.tool || null,
  totalUsers: data.totalUsers ?? 0,
  entries: data.entries ?? 0,
  users: data.users ?? 0,
  adoption: data.adoption ?? 0,
  avgPerUser: data.avgPerUser ?? 0,
  firstDate: data.firstDate || null,
  lastDate: data.lastDate || null,
  volumeSplit: data.volumeSplit || [],
  consistencySplit: data.consistencySplit || [],
  weekday: data.weekday || [],
  detail: data.detail || [],
  entriesTrend: (data.dailyTrend || []).map((day) => day.entries || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

export const fetchCompanySelfDevelopmentOverview = async (department = '') => {
  try {
    return adaptOverview(unwrap(await getCompanySelfDevelopmentDashboard(department)));
  } catch (error) {
    console.error('fetchCompanySelfDevelopmentOverview error:', error);
    return null;
  }
};

export const fetchCompanySelfDevelopmentTool = async (tool, department = '') => {
  try {
    return adaptTool(unwrap(await getCompanySelfDevelopmentTool(tool, department)));
  } catch (error) {
    console.error('fetchCompanySelfDevelopmentTool error:', error);
    return null;
  }
};

// Matches the SelfDevelopmentUsersTable fetcher contract.
export const fetchCompanySelfDevelopmentUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanySelfDevelopmentUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanySelfDevelopmentUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's practice ({ user, tools, toolMix, dailyTrend,
 * activeDays }). Six counts and the outcome each tool tracks — there is no
 * entry list, because there is no version of an entry list that isn't somebody's
 * diary. The backend 404s any user outside this company, so no scoping is
 * needed here.
 */
export const fetchCompanySelfDevelopmentUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanySelfDevelopmentUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanySelfDevelopmentUserDetail error:', error);
    return null;
  }
};
